import Fuse from 'fuse.js';
import { highlight } from '../utils/fuse';

export const STYLES = `
<style>
.searchable_select__input input {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #000;
  border-radius: 8px;
  position: relative;
  font-size: 1rem;
  box-sizing: border-box;
}

.searchable_select__input {
   position: relative;
}

.search-icon {
  background-image: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' role='presentation' class='icon icon-search' viewBox='0 0 37 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M35.6 36l-9.8-9.8c4.1-5.4 3.6-13.2-1.3-18.1-5.4-5.4-14.2-5.4-19.7 0-5.4 5.4-5.4 14.2 0 19.7 2.6 2.6 6.1 4.1 9.8 4.1 3 0 5.9-1 8.3-2.8l9.8 9.8c.4.4.9.6 1.4.6s1-.2 1.4-.6c.9-.9.9-2.1.1-2.9zm-20.9-8.2c-2.6 0-5.1-1-7-2.9-3.9-3.9-3.9-10.1 0-14C9.6 9 12.2 8 14.7 8s5.1 1 7 2.9c3.9 3.9 3.9 10.1 0 14-1.9 1.9-4.4 2.9-7 2.9z'/%3E%3C/svg%3E");
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  position: absolute;
  right: 16px;
  top: 50%;
  translate: 0 -50%;
  border: none;
  width: 18px;
  height: 18px;
}


.active .searchable_select__input .caret {
  rotate: 0deg;
}
</style>
`;

export class SearchableDropdownSelect extends HTMLElement {
  public _internals: ElementInternals | null = null;
  private fuse: Fuse<any> | null;
  private alwaysVisibleOptions: any[];
  private options: any[];
  private wrapper: HTMLElement | null;
  private readonly inputElement: HTMLInputElement | null;
  private optionsElement: Element | undefined;
  private is_active = false;
  private readonly multiple = false;
  private note: Element | undefined | null;
  public selected: string[];
  protected MAX_OPTIONS_LIMIT = 100;

  protected ID: string;
  protected ariaControls: NodeListOf<Element>;
  protected caretButton: HTMLElement | null;

  protected highlightClassName = 'highlight';
  protected highlightMatches = false;
  protected showAfterType = false;
  protected truncateSearchResults = false;

  constructor() {
    super();
    this._internals = this.attachInternals();
    this.attachShadow({ mode: 'open' });
    if (!this.shadowRoot) throw new Error('Shadow root not found');
    this.ID =
      this.getAttribute('id') ||
      'searchable-select-' + Math.random().toString(36).substring(2, 9);
    this.shadowRoot.innerHTML = `
              ${STYLES}
              <div class="searchable_select">
                <div class="searchable_select__input">
                    <input  type="text" placeholder="Search for an option"
                     role="combobox"
                     aria-controls="${this.ID}"
                     aria-autocomplete="list"
                     aria-expanded="false"
                    >
                    <button class="search-icon"
                        aria-expanded="false"
                        aria-controls="${this.ID}"
                    >
                    </button>
                </div>
                <div class="searchable_select__selected"></div>
                <div class="searchable_select__options">
                    <slot name="options" id="${this.ID}" aria-label="expand the options list" ></slot>
                </div>
              </div>
            `;
    this.fuse = null;
    this.alwaysVisibleOptions = [];
    this.options = [];
    this.wrapper = this.shadowRoot.querySelector('.searchable_select');
    this.caretButton = this.shadowRoot.querySelector('.caret');
    this.inputElement = this.shadowRoot.querySelector(
      '.searchable_select__input input'
    );
    this.optionsElement =
      this.shadowRoot.querySelector(
        '.searchable_select__options slot[name="options"]'
      ) || undefined;

    this.ariaControls = this.shadowRoot.querySelectorAll(
      `[aria-controls="${this.ID}"]`
    );
    this.multiple = false;
    this.selected = [];
    this.active = false;

    this.showAfterType = this.hasAttribute('show-after-type');
    this.truncateSearchResults = this.hasAttribute('truncate-search-results');
    this.highlightMatches = this.hasAttribute('highlight-matches');
    this.highlightClassName =
      this.getAttribute('highlight-class') || 'highlight';
    this.MAX_OPTIONS_LIMIT =
      parseInt(this.getAttribute('unfiltered-options-limit') || '100', 10) ||
      100;
    this.note = null;
  }

  connectedCallback() {
    this.fuse = new Fuse(this.options, {
      keys: ['innerText'],
      threshold: 0.3,
      includeMatches: true,
    });

    setTimeout(() => {
      const options = this.shadowRoot
        ?.querySelector<HTMLSlotElement>('slot[name="options"]')
        ?.assignedElements({ flatten: true })
        ?.flatMap((list) => {
          return Array.from(list.querySelectorAll('[role="option"]'))
            .map((option) => {
              if (!(option instanceof HTMLElement)) {
                return null;
              }

              return {
                innerText: option.innerText,
                element: option,
              };
            })
            .filter(Boolean);
        });

      this.note = this.shadowRoot
        ?.querySelector<HTMLSlotElement>('slot[name="options"]')
        ?.assignedElements()
        ?.find((element) => element.tagName.toLowerCase() === 'ul')
        ?.querySelector('li[role="note"]');

      if (!options) {
        return;
      }

      const isAlwaysVisibleOption = (option: { element: any } | null) =>
        option &&
        option.element instanceof HTMLElement &&
        option.element.hasAttribute('data-always-visible');

      this.options = options.filter((option) => !isAlwaysVisibleOption(option));
      this.alwaysVisibleOptions = options.filter(isAlwaysVisibleOption);
      this.optionsElement = this.shadowRoot
        ?.querySelector<HTMLSlotElement>('slot[name="options"]')
        ?.assignedElements({ flatten: true })[0];

      this.fuse?.setCollection(this.options);
      this.renderOptions();
    }, 1);
    this.classList.add('loaded');

    this.inputElement?.addEventListener('input', this.handleInput.bind(this));
    this.inputElement?.addEventListener(
      'keyup',
      this.handleInputKeyUpEvent.bind(this)
    );
    this.inputElement?.addEventListener(
      'focus',
      this.activateOptions.bind(this)
    );
    this.optionsElement?.addEventListener(
      'click',
      this.handleOptionSelection.bind(this)
    );
    this.optionsElement?.addEventListener(
      'keyup',
      this.handleOptionSelection.bind(this)
    );
    this.caretButton?.addEventListener('click', this.toggleOptions.bind(this));
    window.addEventListener('click', this.deactivateOptions.bind(this));
    document.addEventListener('focus', this.deactivateOptions.bind(this));
    this.enableArrowNavigation();
  }

  disconnectedCallback() {
    this.inputElement?.removeEventListener('input', this.handleInput);
    this.inputElement?.removeEventListener('focus', this.activateOptions);
    this.inputElement?.removeEventListener('blur', this.deactivateOptions);
    this.optionsElement?.removeEventListener(
      'click',
      this.handleOptionSelection.bind(this)
    );
    this.optionsElement?.removeEventListener(
      'keyup',
      this.handleOptionSelection.bind(this)
    );
    this.caretButton?.removeEventListener('click', this.toggleOptions);
    window.removeEventListener('click', this.deactivateOptions);
    document.removeEventListener('focus', this.deactivateOptions);
  }

  toggleOptions() {
    this.active = !this.is_active;
  }

  activateOptions() {
    if (this.is_active) {
      return;
    }

    if (this.isQuerySizeInsufficient()) {
      this.active = false;
      return;
    }

    this.active = true;
  }

  deactivateOptions(e: Event) {
    if (!this.is_active) {
      return;
    }

    const target = e.target;

    if (
      this.inputElement instanceof HTMLInputElement &&
      this.inputElement?.value !== this.selected.join(', ') &&
      this.inputElement.value !== ''
    ) {
      this.inputElement.value = this.selected.join(', ');
    }

    if (
      this.inputElement instanceof HTMLInputElement &&
      this.inputElement.value === ''
    ) {
      this.selected = [];
      this.dispatchEvent(
        new CustomEvent('change', {
          detail: {
            value: '',
            selected: this.selected,
          },
        })
      );
    }

    if (
      target instanceof HTMLElement &&
      this !== e.target &&
      !this.contains(target)
    ) {
      this.active = false;
    }
  }

  set active(value: boolean) {
    this.is_active = value;
    if (value) {
      this.wrapper?.classList.add('active');
      this.classList.add('active');
      this.dispatchEvent(new Event('options:activated'));
      this.ariaControls.forEach((element) => {
        element.setAttribute('aria-expanded', 'true');
      });
    } else {
      this.wrapper?.classList.remove('active');
      this.classList.remove('active');
      this.dispatchEvent(new Event('options:deactivated'));
      this.ariaControls.forEach((element) => {
        element.setAttribute('aria-expanded', 'false');
      });
    }
    this.renderOptions();
  }

  get results() {
    if (this.inputElement?.value === '') {
      return this.options.slice(0, this.MAX_OPTIONS_LIMIT);
    }

    return this.fuse?.search(`${this.inputElement?.value}`, {
      limit: this.truncateSearchResults ? this.MAX_OPTIONS_LIMIT : -1,
    });
  }

  handleInputKeyUpEvent(event: Event) {
    const e = event as KeyboardEvent;
    if (e.key === 'Escape') {
      this.active = false;
      return;
    }

    if (e.key === 'Enter') {
      const option = this.optionsElement?.querySelector(
        '[role="option"]:first-child'
      );

      if (option && option instanceof HTMLElement) {
        option.click();
      }
    }

    if (!this.is_active) {
      this.is_active = true;
    }
  }

  isQuerySizeInsufficient() {
    return (
      this.showAfterType &&
      this.inputElement &&
      this.inputElement?.value.length < 1
    );
  }

  handleInput(event: Event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    const query = target.value;

    if (this.isQuerySizeInsufficient()) {
      this.selected = [];
      this.active = false;
      this.renderOptions([]);
      return;
    }

    if (query === '') {
      this.selected = [];
      this.renderOptions();
      return;
    }

    const results = this.fuse?.search(query);
    this.active = true;
    this.renderOptions(results);
  }

  handleOptionSelection(event: Event) {
    const option = event.target;

    if (event.type === 'keyup' && (event as KeyboardEvent).key === 'Escape') {
      this.active = false;
      return;
    }

    if (event.type === 'keyup' && (event as KeyboardEvent).key !== 'Enter') {
      return;
    }

    if (
      !(option instanceof HTMLElement) ||
      !option.matches('[role="option"]')
    ) {
      return;
    }

    if (!this.multiple) {
      this.selected = [];
    }

    const value = option.hasAttribute('value')
      ? option.getAttribute('value')
      : option.innerText;

    if (value) {
      this.selected.push(value);
    }

    if (this.inputElement) {
      this.inputElement.value = this.selected.join(', ');
    }

    this.dispatchEvent(
      new CustomEvent('select', {
        detail: {
          value,
          selected: this.selected,
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          value,
          selected: this.selected,
        },
      })
    );
    this.active = false;
  }

  renderOptions(results = this.results) {
    if (this.isQuerySizeInsufficient()) {
      if (this.optionsElement instanceof HTMLElement) {
        this.optionsElement.innerHTML = '';
      }
      return;
    }

    if (!results) {
      return;
    }

    if (!(this.optionsElement instanceof HTMLElement)) {
      return;
    }

    this.optionsElement.innerHTML = '';

    if (!this.is_active) {
      return;
    }

    if (this.highlightMatches && this.inputElement?.value !== '') {
      results = highlight(results, this.highlightClassName) || results;
    }

    if (this.alwaysVisibleOptions.length > 0 && results) {
      results.push(...this.alwaysVisibleOptions);
    }

    results?.forEach((option) => {
      const element =
        'item' in option
          ? option.item.element.cloneNode(true)
          : option.element.cloneNode(true);
      const value = element.hasAttribute('value')
        ? element.getAttribute('value')
        : element.innerText;

      if (this.highlightMatches) {
        element.innerHTML = option.innerText;
      }

      if (
        this.inputElement?.value !== '' &&
        results &&
        results.indexOf(option) === 0
      ) {
        element.classList.add('best-match');
      }

      if (this.selected.includes(value)) {
        element.classList.add('selected-item');
      }
      this.optionsElement?.appendChild(element);
    });

    if (this.note && this.inputElement?.value === '') {
      this.optionsElement?.appendChild(this.note);
    }

    this.dispatchEvent(
      new CustomEvent('render:options', {
        detail: {
          value: this.inputElement?.value,
        },
      })
    );
  }

  enableArrowNavigation() {
    [this.inputElement, this.optionsElement].forEach((element) => {
      element?.addEventListener('keydown', (e) => {
        const options =
          this.optionsElement?.querySelectorAll<HTMLElement>('[role="option"]');
        if (!options) {
          return;
        }

        if (!(e instanceof KeyboardEvent)) {
          return;
        }

        const currentIndex =
          Array.from(options).findIndex((option) => option.matches(':focus')) ||
          0;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (currentIndex < options.length - 1) {
            options[currentIndex + 1].focus();
          } else {
            options[0].focus();
          }
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (currentIndex > 0) {
            options[currentIndex - 1].focus();
          } else {
            options[options.length - 1].focus();
          }
        }
      });
    });
  }

  clear() {
    this.selected = [];
    if (this.inputElement) {
      this.inputElement.value = '';
    }

    const changeEvent = new CustomEvent('change', {
      detail: {
        value: '',
        selected: this.selected,
      },
    });
    this.dispatchEvent(changeEvent);
  }
}
